import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { RouteId } from '../app.routes';
import { LocaleService } from '../core/locale/locale.service';
import { PalfriesCatalogId } from '../customizations/palfries/palfries.constants';
import { VsbbCatalogId } from '../customizations/vsbb/vsbb.constants';
import { ResetObjectSelection } from '../root-store/actions';
import { LoadCart } from '../root-store/cart-store/actions';
import {
  selectCartItemCount,
  selectCartModel,
  selectOrder,
} from '../root-store/cart-store/selectors';
import { selectCatalog, selectCatalogSettings } from '../root-store/catalog-store/selectors';
import { RootState } from '../root-store/root-state.model';
import {
  selectAdminCartMode,
  selectOrderId,
  selectOrderIdStatusPage,
  selectRouteId,
} from '../root-store/router/selectors';
import { LoadUser } from '../root-store/user-store/actions';
import { selectIsAuthenticated, selectUser } from '../root-store/user-store/selectors';
import { DialogService, panelClassMobileFullscreen } from '../services/dialog.service';
import { LoginService } from '../services/login.service';
import { ResponsiveDesignService } from '../services/responsive-design.service';
import { LanguageSwitchComponent } from '../shared/language-switch/language-switch.component';
import { TippyDirective } from '../shared/tippy.directive';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    TranslateDirective,
    TippyDirective,
    MatIconModule,
    MatToolbarModule,
    MatBadgeModule,
    MatDividerModule,
    MatButtonModule,
    MatMenuModule,
    LanguageSwitchComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.fixed-header]': 'fixedHeader()',
  },
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly localeService = inject(LocaleService);
  private readonly loginService = inject(LoginService);
  private readonly responsive = inject(ResponsiveDesignService);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);

  public readonly catalog = this.store.selectSignal(selectCatalog)();
  public readonly settings = this.store.selectSignal(selectCatalogSettings)();
  public readonly activeRouteId = this.store.selectSignal(selectRouteId);
  RouteId = RouteId;

  user = this.store.selectSignal(selectUser);
  cartCountActual = this.store.selectSignal(selectCartItemCount);
  isAuthenticated = this.store.selectSignal(selectIsAuthenticated);
  order = this.store.selectSignal(selectOrder);
  orderId = this.store.selectSignal(selectOrderId);
  isSmallScreen = this.responsive.isSmallScreen;
  cart = this.store.selectSignal(selectCartModel);
  currentLang = this.localeService.currentLang;
  adminCartMode = this.store.selectSignal(selectAdminCartMode);

  fixedHeader = computed(() => {
    const adminMode = this.adminCartMode();
    const activeRouteId = this.activeRouteId();

    return adminMode && activeRouteId === RouteId.Cart;
  });

  private subs = new SubSink();
  showLogoOnly = false;
  public catalogIds = {
    palfries: PalfriesCatalogId,
    vsbb: VsbbCatalogId,
  };

  cartCount = computed(() => {
    const count = this.cartCountActual();

    return !count || count < 99 ? count : 99;
  });

  ngOnInit() {
    // if cart is not yet loaded, load cart once at Startup (except if cart-component is currently shown)
    const activeRouteId = this.activeRouteId();

    if (
      !this.cart() &&
      activeRouteId !== RouteId.Cart &&
      activeRouteId !== RouteId.OrderStatus &&
      activeRouteId !== RouteId.OrderSuccess
    ) {
      this.store.dispatch(LoadCart());
    }

    // handle display in iFrame: when order-status is shown in iframe, hide and disable all links
    // this is when the order-status is shown via my-orders
    if (window.self !== window.top) {
      this.store
        .select(selectOrderIdStatusPage)
        .subscribe((orderId) => (this.showLogoOnly = !!orderId));
    }
  }

  onHomeClick(): void {
    this.store.dispatch(ResetObjectSelection());
  }

  async openMyOrders() {
    this.dialog.open((await import('../user/my-orders/my-orders.component')).MyOrdersComponent, {
      autoFocus: false,
      panelClass: panelClassMobileFullscreen,
    });
  }

  onEditProfileClick(): void {
    this.dialogService.openEditProfile();
  }

  onPasswordChangeClick(): void {
    this.dialogService.openChangePassword();
  }

  async onLoginClick() {
    this.dialog.open((await import('../user/login/login.component')).LoginComponent);
  }

  onLogoutClick(): void {
    this.loginService.logout().subscribe(() => this.store.dispatch(LoadUser()));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
